import { TableInput } from '@repo/go-table';
import { ColorPicker, Tooltip } from '@repo/go-ui';
import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { wrap } from 'src/packages/mobx-di/wrap';

import type { TableInputProps } from '@repo/go-table';
import type { ChangeEventHandler } from 'react';

import { getTextColorByBackground, normalizeColorToHex } from './utils';

import styles from './color-picker-component.module.scss';

type Props = {
  value: string | null;
  className?: string;
  isEditing?: boolean;
  cellParams?: TableInputProps['cellParams'];
  onChange?(value: string): void;
  onBlur?(): void;
};

export const ColorPickerComponent = wrap<Props>(function ColorPickerComponent({
  value,
  className,
  isEditing,
  cellParams,
  onChange,
  onBlur,
}) {
  const { t } = useTranslation();

  const [draftColor, setDraftColor] = useState(value ?? '');
  const [isPopoverOpen, setIsPopoverOpen] = useState(!!isEditing);

  useEffect(() => {
    if (!isEditing) {
      setIsPopoverOpen(false);
    }
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing) {
      setDraftColor(value ?? '');
    }
  }, [isEditing, value]);

  const normalizedColor = normalizeColorToHex(draftColor);
  const isDraftInvalid = draftColor !== '' && !normalizedColor;
  const previewColor = normalizedColor ?? value;

  const commitDraftColor = () => {
    if (!normalizedColor) {
      return;
    }

    if (normalizedColor !== value) {
      onChange?.(normalizedColor);
    }

    onBlur?.();
    setIsPopoverOpen(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setIsPopoverOpen(isOpen);

    if (!isOpen) {
      commitDraftColor();
    }
  };

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const nextDraftColor = e.target.value;
    const nextNormalizedColor = normalizeColorToHex(nextDraftColor);

    setDraftColor(nextDraftColor);

    if (nextNormalizedColor && nextNormalizedColor !== value) {
      onChange?.(nextNormalizedColor);
    }
  };

  if (!isEditing || !cellParams) {
    return (
      <div
        className={clsx(styles.color, className)}
        style={{ backgroundColor: value ?? undefined, color: getTextColorByBackground(value) }}
      >
        {value ?? '-'}
      </div>
    );
  }

  return (
    <Tooltip
      open={isPopoverOpen}
      trigger={[]}
      placement="bottom-start"
      delay={0}
      gap={10}
      style={{ width: 300 }}
      title={<ColorPicker showSaturation value={previewColor} format="hex" onChange={setDraftColor} />}
      onOpenChange={handleOpenChange}
      withArrow={false}
    >
      <TableInput
        cellParams={cellParams}
        className={clsx(styles.color, className)}
        inputClassName={styles.input}
        placeholder={t('common:placeholders.enterValue')}
        style={{ backgroundColor: previewColor ?? undefined, color: getTextColorByBackground(previewColor) }}
        value={draftColor}
        status={isDraftInvalid ? 'error' : undefined}
        allowClear={false}
        onChange={handleInputChange}
      />
    </Tooltip>
  );
});
